import React from "react";
import moment from "moment";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Icon from "@mui/material/Icon";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import GoalButton from "layouts/goals/components/GoalButton";
import GoalActionButton from "layouts/goals/components/GoalActionButton";
import GoalPill from "layouts/goals/components/GoalPill";
import { END_REASON_LABELS, formatMoney, stateOf, typeOf } from "layouts/assets/assets.constants";

const formatDate = (date) => (date ? moment(date).format("DD MMM YYYY") : "—");

function AssetLine({ asset, detail, actions }) {
  const state = stateOf(asset.state);
  return (
    <SuiBox
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      gap={2}
      py={1}
      sx={{ borderTop: "1px solid #eef0f4" }}
    >
      <SuiBox display="flex" alignItems="center" gap={1.5}>
        <Icon color="secondary">{typeOf(asset.type)?.icon || "devices_other"}</Icon>
        <SuiBox display="flex" flexDirection="column">
          <SuiBox display="flex" alignItems="center" gap={1}>
            <SuiTypography variant="button" fontWeight="medium">
              {asset.name}
            </SuiTypography>
            {asset.state === "in_repair" && <GoalPill color={state.color}>{state.label}</GoalPill>}
          </SuiBox>
          <SuiTypography variant="caption" color="secondary">
            {[asset.serial_number, detail].filter(Boolean).join(" · ")}
          </SuiTypography>
        </SuiBox>
      </SuiBox>
      {actions && (
        <SuiBox display="flex" gap={1}>
          {actions}
        </SuiBox>
      )}
    </SuiBox>
  );
}

function Group({ title, empty, children, count }) {
  return (
    <SuiBox mb={3}>
      <SuiTypography variant="button" fontWeight="bold" display="block" mb={0.5}>
        {title}
      </SuiTypography>
      {count === 0 ? (
        <SuiTypography variant="caption" color="secondary">
          {empty}
        </SuiTypography>
      ) : (
        children
      )}
    </SuiBox>
  );
}

// Everything the company has given this person, and what they were given for good. Return / dispose / assign open the same
// dialogs the Assets page uses (handled by the parent).
function EmployeeAssetsModal({ open, onClose, employee, groups, onAssign, onReturn, onDispose }) {
  if (!employee) return null;
  const { held, received } = groups || { held: [], received: [] };
  const leaving = employee.employment_status !== "active";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Assets · {employee.name || employee.user_name}</DialogTitle>
      <DialogContent>
        {leaving && held.length > 0 && (
          <SuiTypography variant="caption" color="warning" display="block" mb={2}>
            This person is{" "}
            {employee.employment_status === "exited" ? "no longer here" : "on notice"} and still
            holds {held.length} company asset{held.length > 1 ? "s" : ""}. Return them, or dispose
            of them if they are being given or sold.
          </SuiTypography>
        )}
        <Group title="Company assets they hold" empty="Nothing assigned." count={held.length}>
          {held.map((asset) => (
            <AssetLine
              key={asset.id}
              asset={asset}
              detail={`since ${formatDate(asset.current_assignment.assigned_at)}`}
              actions={
                <>
                  <GoalActionButton
                    icon="assignment_return"
                    tooltip="Return to stock"
                    color="info"
                    onClick={() => onReturn(asset)}
                  />
                  <GoalActionButton
                    icon="archive"
                    tooltip="Dispose (severance, sold...)"
                    color="warning"
                    onClick={() => onDispose(asset)}
                  />
                </>
              }
            />
          ))}
        </Group>
        <Group
          title="Received from the company"
          empty="Nothing given or sold."
          count={received.length}
        >
          {received.map((asset) => {
            return (
              <AssetLine
                key={asset.id}
                asset={asset}
                detail={`${END_REASON_LABELS[asset.disposal_reason] || "Disposed"} on ${formatDate(
                  asset.disposal_date
                )}${
                  asset.disposal_price !== null
                    ? ` for ${formatMoney(asset.disposal_price, asset.currency)}`
                    : ""
                }`}
              />
            );
          })}
        </Group>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <GoalButton variant="secondary" onClick={onClose}>
          Close
        </GoalButton>
        <GoalButton variant="primary" icon="person_add" onClick={onAssign}>
          Assign asset
        </GoalButton>
      </DialogActions>
    </Dialog>
  );
}

export default EmployeeAssetsModal;
